import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail } from "lucide-react";

const LOGO_URL = "/absolin-logo.png";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.success) {
      navigate("/");
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="login-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 25% 25%, #3B82F6 0%, transparent 50%), radial-gradient(circle at 75% 75%, #1E40AF 0%, transparent 50%)"
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-4 mb-2">
            <img src={LOGO_URL} alt="Absolin" className="h-14 w-auto rounded-md" data-testid="login-logo-desktop" />
            <div className="border-l border-slate-700 pl-4">
              <div className="text-xs text-slate-400 uppercase tracking-widest font-mono">SQL Repository</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Infinite Digital Possibilities</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              The single source of truth for your <span className="text-blue-400">ERP SQL queries</span>.
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
              Catalog, search, and version control your MS SQL queries across Focus 6, Focus 7, Focus 9/X, and Web Apps.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-800">
            <div>
              <div className="font-heading font-black text-2xl text-blue-400">7</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">Query Types</div>
            </div>
            <div>
              <div className="font-heading font-black text-2xl text-blue-400">5</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">Products</div>
            </div>
            <div>
              <div className="font-heading font-black text-2xl text-blue-400">∞</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">Versions</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500 font-mono">
          © {new Date().getFullYear()} Absolin · Infinite Digital Possibilities
        </div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-md flex items-center justify-center overflow-hidden p-1.5">
              <img src={LOGO_URL} alt="Absolin" className="w-full h-full object-contain" data-testid="login-logo-mobile" />
            </div>
            <div>
              <div className="font-heading font-black text-lg tracking-tight lowercase">absolin<span className="text-[10px] align-top text-blue-600 ml-0.5">®</span></div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">SQL Repository</div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-black tracking-tight text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500 mt-2">Access your SQL query repository</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@absolin.com"
                  className="pl-10 h-11 border-slate-200 focus-visible:ring-blue-500"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-11 border-slate-200 focus-visible:ring-blue-500"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-blue-700 hover:bg-blue-800 text-white font-medium"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Default Admin</div>
            <div className="text-xs font-mono text-slate-700">admin@absolin.com / Admin@123</div>
          </div>
        </div>
      </div>
    </div>
  );
}
